/**
 * ConvergenceChart — Pure SVG line chart for convergence by pass.
 *
 * Per PRD Appendix C / FR-009:
 *   - SVG root with role="img" and aria-label
 *   - Two horizontal dashed threshold reference lines (converged + minimum-viable)
 *   - Oscillation flag markers on flagged passes
 *   - Screen-reader-only tabular fallback (promoted under reduced motion)
 */
import React from "react";
import { useReducedMotion } from "../../hooks/useReducedMotion";
import type { ConvergenceChartProps } from "./types";

const CHART_WIDTH = 400;
const CHART_HEIGHT = 200;
const PADDING_LEFT = 40;
const PADDING_RIGHT = 20;
const PADDING_TOP = 20;
const PADDING_BOTTOM = 30;

const PLOT_WIDTH = CHART_WIDTH - PADDING_LEFT - PADDING_RIGHT;
const PLOT_HEIGHT = CHART_HEIGHT - PADDING_TOP - PADDING_BOTTOM;

export default function ConvergenceChart({
  data,
  thresholdConverged,
  thresholdMinimumViable,
  ariaLabel = "Convergence by pass",
}: ConvergenceChartProps): React.ReactElement {
  const prefersReduced = useReducedMotion();

  // Scale helpers
  const maxPass = Math.max(...data.map((d) => d.passNumber), 1);
  const xScale = (passNumber: number): number =>
    PADDING_LEFT + ((passNumber - 1) / Math.max(maxPass - 1, 1)) * PLOT_WIDTH;
  const yScale = (score: number): number =>
    PADDING_TOP + (1 - score) * PLOT_HEIGHT;

  // Build the line path
  const sorted = [...data].sort((a, b) => a.passNumber - b.passNumber);
  const linePath = sorted
    .map((d, i) => `${i === 0 ? "M" : "L"} ${xScale(d.passNumber)} ${yScale(d.score)}`)
    .join(" ");

  return (
    <div>
      {/* SVG chart */}
      <svg
        role="img"
        aria-label={ariaLabel}
        viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
        width={CHART_WIDTH}
        height={CHART_HEIGHT}
        style={{ display: prefersReduced ? "none" : "block" }}
      >
        <title>{ariaLabel}</title>

        {/* Y-axis labels */}
        <text
          x={PADDING_LEFT - 8}
          y={PADDING_TOP}
          textAnchor="end"
          dominantBaseline="central"
          style={{ fontSize: "10px", fontFamily: "var(--font-mono)", fill: "var(--fg2, #6b7280)" }}
        >
          1.0
        </text>
        <text
          x={PADDING_LEFT - 8}
          y={PADDING_TOP + PLOT_HEIGHT}
          textAnchor="end"
          dominantBaseline="central"
          style={{ fontSize: "10px", fontFamily: "var(--font-mono)", fill: "var(--fg2, #6b7280)" }}
        >
          0.0
        </text>

        {/* Threshold: converged (upper dashed line) */}
        <line
          x1={PADDING_LEFT}
          y1={yScale(thresholdConverged)}
          x2={CHART_WIDTH - PADDING_RIGHT}
          y2={yScale(thresholdConverged)}
          stroke="var(--color-success, #0d8a5e)"
          strokeDasharray="6 3"
          strokeWidth={1}
        />
        <text
          x={CHART_WIDTH - PADDING_RIGHT + 2}
          y={yScale(thresholdConverged)}
          dominantBaseline="central"
          style={{ fontSize: "9px", fontFamily: "var(--font-mono)", fill: "var(--color-success, #0d8a5e)" }}
        >
          {thresholdConverged}
        </text>

        {/* Threshold: minimum viable (lower dashed line) */}
        <line
          x1={PADDING_LEFT}
          y1={yScale(thresholdMinimumViable)}
          x2={CHART_WIDTH - PADDING_RIGHT}
          y2={yScale(thresholdMinimumViable)}
          stroke="var(--color-warning, #c2850c)"
          strokeDasharray="6 3"
          strokeWidth={1}
        />
        <text
          x={CHART_WIDTH - PADDING_RIGHT + 2}
          y={yScale(thresholdMinimumViable)}
          dominantBaseline="central"
          style={{ fontSize: "9px", fontFamily: "var(--font-mono)", fill: "var(--color-warning, #c2850c)" }}
        >
          {thresholdMinimumViable}
        </text>

        {/* Score line */}
        <path
          d={linePath}
          fill="none"
          stroke="var(--color-action-blue, #1863dc)"
          strokeWidth={2}
        />

        {/* Data points */}
        {sorted.map((d) => (
          <circle
            key={d.passNumber}
            cx={xScale(d.passNumber)}
            cy={yScale(d.score)}
            r={4}
            fill={d.oscillationFlag ? "var(--color-error, #d9363e)" : "var(--color-action-blue, #1863dc)"}
            stroke="var(--bg-primary, #fff)"
            strokeWidth={1.5}
          >
            <title>
              Pass {d.passNumber}: {d.score.toFixed(3)}
              {d.oscillationFlag ? " (oscillation)" : ""}
            </title>
          </circle>
        ))}

        {/* Oscillation flag markers */}
        {sorted
          .filter((d) => d.oscillationFlag)
          .map((d) => (
            <text
              key={`osc-${d.passNumber}`}
              x={xScale(d.passNumber)}
              y={yScale(d.score) - 10}
              textAnchor="middle"
              style={{ fontSize: "10px", fontFamily: "var(--font-mono)", fill: "var(--color-error, #d9363e)" }}
              aria-hidden="true"
            >
              !
            </text>
          ))}

        {/* X-axis labels */}
        {sorted.map((d) => (
          <text
            key={`x-${d.passNumber}`}
            x={xScale(d.passNumber)}
            y={CHART_HEIGHT - 8}
            textAnchor="middle"
            style={{ fontSize: "10px", fontFamily: "var(--font-mono)", fill: "var(--fg2, #6b7280)" }}
          >
            {d.passNumber}
          </text>
        ))}
      </svg>

      {/* Screen-reader-only fallback table */}
      <table className={prefersReduced ? "" : "sr-only"} aria-label={ariaLabel}>
        <caption>{ariaLabel}</caption>
        <thead>
          <tr>
            <th scope="col">Pass</th>
            <th scope="col">Score</th>
            <th scope="col">Status</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((d) => (
            <tr key={d.passNumber}>
              <td>{d.passNumber}</td>
              <td>{d.score.toFixed(3)}</td>
              <td>{d.oscillationFlag ? "oscillation" : "stable"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
